import { useCallback, useMemo } from "react";
import { CustomPiecesConstants } from "../../../utils/constants";
import "./customPieceHook.scss";
import { Square } from "chess.js";
import { useGame } from "../../../context/GameContext";
import FallingKing from "../animations/FallingKing";

/**
 * Creates custom chess piece components with their own images and styles.
 * The images are pulled from the public/images directory.
 * Each piece has a name corresponding to the image file name.
 * (e.g. "wP.webp") would be the white pawn image.
 *
 * @param hoveredSquare the square that the mouse is hovering over.
 * @param clickedSquare the square that the mouse is clicked on.
 * @returns a dictionary of custom chess piece components.
 */
export const useCustomPieces = (
    hoveredSquare: Square | null,
    clickedSquare: Square | null,
    onSquareClick: (square: Square) => void,
    remoteSquare: Square | null,
) => {
    const { gameState } = useGame();

    /**
     * Calculates the bottom offset for the chess piece.
     * Centers the piece vertically within the tile.
     * @param squareWidth the width of the square.
     * @returns the CSS setting for the bottom offset of the chess piece.
     */
    const calculateBottomOffset = useCallback((squareWidth: number) => {
        const tilePos = CustomPiecesConstants.TILE_WIDTH_OFFSET * squareWidth;
        return `${tilePos - (CustomPiecesConstants.ZOOM_FACTOR - 1) * squareWidth}px`;
    }, []);

    /**
     * Calculates the left offset for the chess piece.
     * Centers the piece horizontally.
     * @param squareWidth the width of the square.
     * @returns the CSS setting for the left offset of the chess piece.
     */
    const calculateLeftOffset = useCallback((squareWidth: number, isDragging: boolean) => {
        return `${((1 - CustomPiecesConstants.ZOOM_FACTOR * (isDragging ? 1.5 : 1)) * squareWidth) / 2}px`;
    }, []);

    return useMemo(() => {
        const pieceComponents: {
            [key: string]: ({
                squareWidth,
                square,
                isDragging,
            }: {
                squareWidth: number;
                square?: Square;
                isDragging?: boolean;
            }) => JSX.Element;
        } = {};
        CustomPiecesConstants.PIECE_DICTIONARY.forEach(({ piece }) => {
            pieceComponents[piece] = ({ squareWidth, square }) => {
                // Guard against undefined square (can happen during initial render/drag)
                if (!square || square.length < 2) {
                    // Still render piece for drag preview even without square
                    return (
                        <div className="piece" style={{ width: squareWidth, height: squareWidth }}>
                            <img
                                src={`images/${piece}.webp`}
                                className="chess-piece-image"
                                width={squareWidth * CustomPiecesConstants.ZOOM_FACTOR}
                                height={squareWidth * CustomPiecesConstants.ZOOM_FACTOR}
                                alt={piece}
                                style={{
                                    position: "absolute",
                                    left: "50%",
                                    top: "50%",
                                    transform: "translate(-50%, -50%)",
                                    objectFit: piece[1] === "K" ? "contain" : "cover",
                                }}
                            />
                        </div>
                    );
                }

                let isHovered = hoveredSquare === square;
                const isRemoteSelected = remoteSquare === square;

                // Disable hover effects on mobile devices
                if (window.innerWidth < 600) {
                    isHovered = false;
                }

                const isCheckmate = gameState?.game?.isCheckmate();

                const isWhiteKing = piece === "wK";
                const isBlackKing = piece === "bK";

                const isCheckmatedWhiteKing =
                    isWhiteKing && isCheckmate && gameState?.game?.turn() === "w";

                const isCheckmatedBlackKing =
                    isBlackKing && isCheckmate && gameState?.game?.turn() === "b";

                const isCheckmatedKing = isCheckmatedWhiteKing || isCheckmatedBlackKing;

                // Calculate z-index based on row position
                // Use higher base (10+) to ensure pieces stay above animated squares
                // Lower rows (1) should appear on top of higher rows (8)
                // Row 1 gets z-index 18, row 2 gets 17, ..., row 8 gets 11
                const row = parseInt(square[1]);
                const baseZIndex = 19 - row;

                return (
                    <div
                        /* The div containing the chess piece */
                        className="piece"
                        id={square}
                        data-testid={square}
                        style={{
                            width: squareWidth,
                            height: squareWidth,
                            animationDelay: `${Math.random() * 0.6}s`,
                            zIndex: isCheckmatedKing ? 100 : baseZIndex,
                        }}
                    >
                        {isCheckmatedKing ? (
                            <FallingKing
                                squareWidth={squareWidth}
                                isWhite={isCheckmatedWhiteKing}
                                calculateLeftOffset={calculateLeftOffset}
                                calculateBottomOffset={calculateBottomOffset}
                                piece={piece}
                            />
                        ) : (
                            <>
                                <img
                                    onClick={() => onSquareClick(square)}
                                    onTouchStart={() => onSquareClick(square)}
                                    src={`images/${piece}.webp`}
                                    className="chess-piece-image"
                                    width={squareWidth * CustomPiecesConstants.ZOOM_FACTOR}
                                    height={squareWidth * CustomPiecesConstants.ZOOM_FACTOR}
                                    alt={piece}
                                    draggable={false}
                                    style={{
                                        left: calculateLeftOffset(squareWidth, false),
                                        bottom: calculateBottomOffset(squareWidth),
                                        objectFit: piece[1] === "K" ? "contain" : "cover",
                                        opacity: isHovered || isRemoteSelected ? 0.6 : 1,
                                    }}
                                />
                                <img
                                    onClick={() => onSquareClick(square)}
                                    onTouchStart={() => onSquareClick(square)}
                                    src={`images/${piece}.webp`}
                                    className="chess-piece-shadow"
                                    width={squareWidth * CustomPiecesConstants.ZOOM_FACTOR}
                                    height={squareWidth * CustomPiecesConstants.ZOOM_FACTOR}
                                    alt={piece}
                                    draggable={false}
                                    style={{
                                        left: calculateLeftOffset(squareWidth, false),
                                        bottom: calculateBottomOffset(squareWidth),
                                        marginBottom: `${squareWidth * 0.3}px`,
                                        objectFit: piece[1] === "K" ? "contain" : "cover",
                                    }}
                                />
                            </>
                        )}
                    </div>
                );
            };
        });
        return pieceComponents;
        // eslint-disable-next-line
    }, [hoveredSquare, clickedSquare, gameState.game]);
};
